import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/**
 * ADM -> Exposure, calea noua. Perechile vechi, din
 * `application/controllers/Adm.php`:
 *
 *   exposure            -> GET /adm/exposure
 *   exposureDetail($p)  -> GET /adm/exposure/{port}
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`.
 *
 * RASPUNDE LA O INTREBARE DE AUDIT, nu de administrare: nu "cum e legat X", ci
 * "ce e deschis si pe cate masini". De aceea randurile sunt grupate pe PORT, nu
 * pe masina: o lista cu un rand pe fiecare proces care asculta are zeci de mii
 * de randuri si nu raspunde la nimic.
 *
 * DOUA SURSE, spuse pe fiecare rand (`source`): `agent` - ascultatorii raportati
 * de agenti - si `scan` - porturile gasite deschise de scanarea releului pe
 * aparatele descoperite, fara cele pe care un agent le spune deja.
 */

/** De unde se stie ca portul e deschis. `''` in filtru = oricare. */
export type ExposureSource = 'agent' | 'scan'

/**
 * O masina, cu ce trebuie ca sa duca la fisa ei: aparatul din inventar
 * (`deviceId`), CI-ul (`ciId`), sau aparatul gasit numai de scanare
 * (`discoveredId` si adresa lui). Zero = nu e de felul ala.
 */
export interface ExposureHost {
  name: string
  /** Numele intreg al aparatului gasit (wks02.cecdemo.local); gol cand nu e altul decat numele. */
  fqdn?: string
  ciId: number
  deviceId: number
  discoveredId: number
  address: string
}

/**
 * Unde e fisa masinii. Aparatul cu agent are fisa lui in inventar; un CI fara
 * aparat, fisa din registru; un aparat gasit numai de scanare n-are fisa a lui,
 * deci duce in lista aparatelor descoperite, cautat dupa adresa.
 */
export function machineHref(host: Pick<ExposureHost, 'name' | 'ciId' | 'deviceId' | 'discoveredId' | 'address'>): string | null {
  if (host.deviceId > 0) return `/app/inventoree/device/${host.deviceId}`
  if (host.ciId > 0) return `/app/cmdb/view/${host.ciId}`
  if (host.discoveredId > 0) {
    const needle = host.address || host.name
    return needle ? `/app/inventoree/live_devices?search=${encodeURIComponent(needle)}` : '/app/inventoree/live_devices'
  }
  return null
}

export interface ExposurePort {
  port: number
  proto: number
  protoName: string
  serviceName: string
  application: string
  machines: number
  onAllInterfaces: number
  asService: number
  /** Pe cate masini a SPUS cineva ceva. Zero inseamna tacere, nu "nu". */
  serviceKnown: number
  lastSeen: string
  /** Port de administrare sau de partajare: "uita-te aici", nu "asta e o gaura". */
  watched: boolean
  watchedName: string
  source: ExposureSource
  /** Primele masini, in ordinea numelui; restul (`machines` - cate sunt aici) in fereastra portului. */
  hosts: ExposureHost[]
}

export interface ExposurePage {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  filters: { port: number; search: string; source: ExposureSource | '' }
  rows: ExposurePort[]
  ports: number
  machines: number
  watched: number
  /** Lista s-a lovit de plafon: ce se vede e un inceput, si ecranul o spune. */
  reachedCeiling: boolean
}

export interface ExposureListener {
  ciId: number
  /** Aparatul din inventar din spatele CI-ului; 0 = nu e unul. */
  deviceId: number
  name: string
  /** Numele intreg al aparatului gasit de scanare; gol pe randurile agentilor. */
  fqdn?: string
  serviceName: string
  application: string
  address: string
  everywhere: boolean
  companyName: string
  updated: string
  source: ExposureSource
  /** Aparatul descoperit, pe randurile de scanare (`ciId` e atunci 0). */
  discoveredId: number
}

export interface ExposureDetail {
  port: number
  proto: number
  protoName: string
  watched: boolean
  watchedName: string
  rows: ExposureListener[]
  reachedCeiling: boolean
}

export function exposureQuery(port: number, search: string, source: ExposureSource | '' = '') {
  const params = new URLSearchParams({ port: String(port), search, source })

  return queryOptions({
    queryKey: ['adm', 'exposure', port, search, source],
    queryFn: () => apiGet<ExposurePage>(`/adm/exposure?${params.toString()}`),
  })
}

export function exposureDetailQuery(port: number, proto: number, source: ExposureSource | '' = '') {
  return queryOptions({
    queryKey: ['adm', 'exposure', 'detail', port, proto, source],
    queryFn: () => apiGet<ExposureDetail>(`/adm/exposure/${port}?proto=${proto}&source=${source}`),
    enabled: port > 0,
  })
}
